import React, { FormEvent } from 'react';
import {
  FieldControl,
  FieldGroup,
  FormBuilder,
  Validators,
} from 'react-reactive-form';
import {
  Modal, ModalHeader, ModalBody
} from 'reactstrap';

import RadioButton from '../forms/RadioButton';
import { CarModelModel, ExcessModel } from '../../api/core/core.types';
import { NumberInput } from '../forms/Input';
import Select from '../forms/Select';
import { MultiSelect } from '../forms/MultiSelect';
import { TwoColumnsSelectValueModel } from '../forms/TwoColumnsSelect';
import {WithTranslation, withTranslation} from 'react-i18next';
import { ClassifierModel } from '../../api/admin/admin.types';

interface ProductCarModelsModalProps extends WithTranslation {
  toggle: () => void;
  isOpen: boolean;
  excessModel?: ProductExcessModel;
  carModels: CarModelModel[];
  currencies: ClassifierModel[];
  onConfirm: (excess: ExcessModel, id?: number) => void;
  onCancel: () => void;
  onDelete: (id: number) => void;
  acrissCodesInUse: Map<number, string[]>;
}

export interface ProductExcessModel {
  id: number;
  excessModel: ExcessModel;
}

interface ProductCarClassState {
  selectedCarClasses: string[];
  acrissCodesInUse: string[];
}

class ProductCarModelsModal extends React.PureComponent<ProductCarModelsModalProps, ProductCarClassState> {

  readonly state = {
    selectedCarClasses: [],
    acrissCodesInUse: []
  };

  form = FormBuilder.group({
    carClassSelection: [ 'all' ],
    carClasses: [ { items: [], selected: [] } ],
    excessCurrencyCode: [ undefined, Validators.required ],
    excessAmount: [ undefined, [ Validators.min(0), Validators.required ] ],
    depositAmount: [ undefined, Validators.min(0) ],
  });

  componentDidMount() {
    this.assignCarModelsToForm(this.props.carModels);
  }

  componentDidUpdate(prevProps: ProductCarModelsModalProps) {
    if (this.props.excessModel !== prevProps.excessModel || this.props.isOpen !== prevProps.isOpen) {
      this.resetFormValues().then((r) => {
        this.assignCarModelsToForm(this.props.carModels);
        this.updateFormValue();
      });

      this.getUsedAcrissCodes();
    }

    if (this.props.excessModel && !prevProps.excessModel) {
      this.updateFormValue();
    }

    if (this.props.carModels.length !== prevProps.carModels.length) {
      this.assignCarModelsToForm(this.props.carModels);
    }
  }

  onDeleteClick() {
    const excess = this.props.excessModel;
    if (excess && excess.id !== null) {
      this.props.onDelete(excess.id);
    }
  }

  assignCarModelsToForm = (carModels: CarModelModel[]) => {
    const prev = this.form.controls.carClasses.value as TwoColumnsSelectValueModel;
    const renderedAcrissCodes: string[] = [];

    carModels.forEach((carModel) => {
      if (!renderedAcrissCodes.find((a) => a === carModel.acrissCode)) {
        prev.items.push(carModel);

        renderedAcrissCodes.push(carModel.acrissCode);
      }
    });

    if (this.props.excessModel) {
      this.props.excessModel.excessModel.acrissCodes.forEach((c) => {
        prev.selected.push(c);
      });
      this.setState({
        selectedCarClasses: this.props.excessModel.excessModel.acrissCodes.map((c) => c)
      });
    }
  };

  handleCarClassSelectValueChange = (v: any) => {
    this.setState({ selectedCarClasses: v.map((itm: any) => itm.acrissCode) });
    this.form.patchValue({
      carClasses: {
        ...this.form.controls.carClasses.value,
        selected: v.map((itm: any) => itm.acrissCode)
      }
    });
  };

  resetFormValues = async () => {
    this.form.reset();
    this.form.controls.carClasses.patchValue({ items: [], selected: [] });
    this.form.controls.carClassSelection.patchValue('all');
  };

  updateFormValue = () => {
    if (!this.props.excessModel) {
      return;
    }

    const currency = this.getCurrency(this.props.excessModel.excessModel.excessCurrencyCode);
    this.form.patchValue({
      excessCurrencyCode: currency ? { label: currency.name, value: currency.code } : undefined,
      depositAmount: this.props.excessModel.excessModel.depositAmount,
      excessAmount: this.props.excessModel.excessModel.excessAmount,
      carClassSelection: this.props.excessModel.excessModel.acrissCodes.length ? 'selection' : 'all',
      carClasses: {
        ...this.form.controls.carClasses.value,
        selected: this.props.excessModel ? this.form.controls.carClasses.value.selected.filter((v: any) =>
          !!this.props.excessModel?.excessModel!.acrissCodes.find((val) => val === v)) : []
      }
    });
    if (this.props.excessModel) this.setState({ selectedCarClasses: this.props.excessModel?.excessModel!.acrissCodes });
  };

  getCurrency = (code: string) => this.props.currencies.find((c) => c.code === code);

  onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const form = this.form.controls;
    this.form.markAsSubmitted();

    if (this.form.invalid) {
      return;
    }

    if (this.form.controls.carClassSelection.value !== 'all' && this.state.selectedCarClasses.length == 0) {
      return;
    }

    const acrissCodes = this.getSelectedAcrissCodes();

    let id: number | undefined;
    if (this.props.excessModel && this.props.excessModel.id >= 0) id = this.props.excessModel.id;

    const excess: ExcessModel = {
      acrissCodes,
      excessAmount: form.excessAmount.value,
      depositAmount: form.depositAmount.value,
      excessCurrencyCode: form.excessCurrencyCode.value.value,
    };

    this.props.onConfirm(excess, id);
  };

  getSelectedAcrissCodes = () => {
    const acrissCodes: string[] = [];

    if (this.form.controls.carClassSelection.value !== 'all') {
      return this.state.selectedCarClasses;
    }
    let noClassesPreviouslySelected = true;
    const keys: number[] = Array.from(this.props.acrissCodesInUse.keys());

    if (!this.props.excessModel) {
      keys.forEach((key) => {
        const carClassAcrissCodes = this.props.acrissCodesInUse.get(key);
        if (carClassAcrissCodes && carClassAcrissCodes.length) noClassesPreviouslySelected = false;
      });
    }

    if (noClassesPreviouslySelected) {
      return [];
    }

    this.props.carModels.forEach((c) => {
      if (!this.isAcrissCodeAssigned(c.acrissCode) && !acrissCodes.find((a) => a === c.acrissCode)) {
        acrissCodes.push(c.acrissCode);
      }
    });

    return acrissCodes;
  };

  isAcrissCodeAssigned = (acrissCode: string) => {
    let isCodeAssigned = false;
    const keys: number[] = Array.from(this.props.acrissCodesInUse.keys());

    keys.forEach((key) => {
      if (!this.props.excessModel || (this.props.excessModel && key !== this.props.excessModel.id)) {
        const acrissCodes = this.props.acrissCodesInUse.get(key);

        if (acrissCodes) {
          if (acrissCodes.find((code) => code === acrissCode)) isCodeAssigned = true;
        }

      }
    });

    return isCodeAssigned;
  };


  getExcessCurrency = () => {
    const excessModel = this.props.excessModel;
    if (excessModel) {
      const defaultCurrency = this.props.currencies.find((c) => c.code === excessModel.excessModel.excessCurrencyCode);
      if (defaultCurrency) return { label: defaultCurrency.name, value: defaultCurrency.code };
    }

    return undefined;
  };

  getUsedAcrissCodes = (): string[] => {
    const keys: number[] = Array.from(this.props.acrissCodesInUse.keys());
    let usedAcrissCodes: string[] = [];

    keys.forEach((key) => {
      if (!this.props.excessModel || (this.props.excessModel && key !== this.props.excessModel.id)) {
        const acrissCodes = this.props.acrissCodesInUse.get(key);

        if (acrissCodes) {
          usedAcrissCodes = [ ...usedAcrissCodes, ...acrissCodes ];
        }
      }
    });

    this.setState({
      acrissCodesInUse: usedAcrissCodes
    }, () => {
      this.forceUpdate();
    });
    return usedAcrissCodes;
  };

  render() {
    const { t } = this.props;

    return (
      <Modal isOpen={this.props.isOpen} toggle={this.props.toggle} autoFocus={false} size={'xl'}>
        <ModalHeader toggle={this.props.toggle}>{ !this.props.excessModel ?
          t('modal.title.add_product_excess') : 'Edit excess'}</ModalHeader>
        <ModalBody>
          <FieldGroup
            strict={false}
            control={this.form}
            render={() => (
              <form className="form-horizontal" onSubmit={(e) => this.onSubmit(e)}>
                <div className="form-group row scrolling-div pb-1">
                  <div className="col-2 d-flex align-items-start justify-content-end">
                    <label>{t('l.product.add_excess.car_classes')}:</label>
                  </div>
                  <div className="col-6 d-flex align-items-start justify-content-start" style={{ paddingLeft: '20px' }}>
                    <>
                      <div className="d-flex flex-column flex-wrap">
                        <div className="pb-1">
                          <FieldControl
                            strict={false}
                            name="carClassSelection"
                            render={(props) => <RadioButton {...props} fieldValue="all" id="car_all">
                              {t('l.selection.all')}
                            </RadioButton>}
                          />
                          <FieldControl
                            strict={false}
                            name="carClassSelection"
                            render={(props) => <RadioButton {...props} fieldValue="selection"
                                                            id="car_sel">
                              {t('l.selection.selected')}
                            </RadioButton>}
                          />
                        </div>
                        {
                          this.form.controls.carClassSelection.value === 'selection'
                          && (
                              <FieldControl
                                  name="carClasses"
                                  render={(v) => (
                                      <MultiSelect
                                          {...v}
                                          selectedAttributeName="acrissCode"
                                          sortAttributeName="acrissCode"
                                          handleValueChange={(val: any) => this.handleCarClassSelectValueChange(val)}
                                          filterAttributes={[ 'name', 'acrissCode' ]}
                                          primaryAtr="acrissCode"
                                          disabledValues={this.state.acrissCodesInUse}
                                          secondaryAtr="name"
                                      />
                                  )}
                              />
                          )
                        }
                      </div>
                    </>
                  </div>
                </div>
                <div>
                  <div className="form-group d-flex">
                    <div className="col-2 d-flex align-items-center justify-content-end">
                      <label>{t('l.product.add_excess.currency')}:</label>
                    </div>
                    <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                      <FieldControl
                        name="excessCurrencyCode"
                        strict={false}
                        render={(props) => (
                          <Select
                            defaultValue={this.getExcessCurrency()}
                            className="mr-1"
                            placeholder="Choose excess currency"
                            // eslint-disable-next-line no-nested-ternary
                            options={this.props.currencies.map((d) =>
                              ({ label: d.stringValue, value: d.code }))
                              .sort((a, b) =>
                                ((a < b) ? -1 : (a > b) ? 1 : 0))}
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            label="excess currency code"
                            onChange={(value) => this.form.patchValue({ excessCurrencyCode: value })}
                          />
                        )}
                      />
                    </div>
                  </div>
                  <div className="form-group d-flex">
                    <div className="col-2 d-flex align-items-center justify-content-end">
                      <label>{t('l.product.add_excess.excess_amount')}:</label>
                    </div>
                    <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                      <FieldControl
                        strict={false}
                        name="excessAmount"
                        render={NumberInput}
                      />
                    </div>
                  </div>
                  <div className="form-group d-flex">
                    <div className="col-2 d-flex align-items-center justify-content-end">
                      <label>{t('l.product.add_excess.deposite_amount')}:</label>
                    </div>
                    <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                      <FieldControl
                        strict={false}
                        name="depositAmount"
                        render={NumberInput}
                      />
                    </div>
                  </div>
                </div>
                <div className={`row ${this.props.excessModel ? 'justify-content-between' : 'justify-content-end'}`}>
                  { this.props.excessModel && (
                  <div>
                    <button type="button" className="btn btn-danger mr-2 mt-1"
                            onClick={(e) => this.onDeleteClick()}>
                      {t('b.delete_Generic delete button').toUpperCase()}
                    </button>
                  </div>
                  )}
                  <div className="mt-1">
                    <button type="button" className="btn btn-outline-secondary btn mr-2"
                            onClick={this.props.onCancel}>
                      {t('b.cancel_Generic cancel button translation').toUpperCase()}</button>
                    <button type="submit" className="btn btn-primary btn"
                            onClick={this.onSubmit}>
                      {t('b.save_Generic save button translation').toUpperCase()}
                    </button>
                  </div>
                </div>
              </form>
            )}
          />
        </ModalBody>
      </Modal>
    );
  }
}

export default withTranslation()(ProductCarModelsModal);
